import { Capacitor } from '@capacitor/core';
import toast from 'react-hot-toast';
import { createAppApiClient } from '@shared/api/createAppApiClient';
import {
  clearAuthSession,
  endSession,
  registerSessionEndHandler,
  getRefreshToken,
  setRefreshToken,
  setAccessToken,
} from '@shared/auth/session';

// Helper to check if current path is a public page that doesn't require auth
const isPublicPath = (pathname: string): boolean => {
  const publicPaths = [
    '/public/',           // All /public/* routes
    '/c/',                // Short calendar URLs
    '/r/',                // Short report URLs
    '/n/',                // Short note URLs
    '/o/',                // Public outreach
    '/results/',          // Public outreach results
    '/campaign-response/', // Campaign response
    '/feedback/',         // Note feedback
    '/calendar-unsubscribe/', // Calendar unsubscribe
    '/login',
    '/pro/login',
    '/register',
    '/forgot-password',
    '/reset-password/',
    '/verify-email/',
    '/accept-invitation/',
    '/join/',             // Organization sign-up code links
  ];

  return publicPaths.some(path => pathname.startsWith(path)) || pathname === '/';
};

const isNative = Capacitor.isNativePlatform();

// The single navigation funnel for any session end (401 interceptor, idle timeout,
// manual logout). endSession() clears state + records the reason; this handler
// sends the user to the correct login route, preserving where they were.
registerSessionEndHandler((_reason, ctx) => {
  const currentPath = window.location.pathname;
  const currentPathWithSearch = currentPath + window.location.search;
  const isCitizenPage = currentPath.startsWith('/citizen/');
  const isProPage = currentPath.startsWith('/pro/');
  const wasCitizen = isCitizenPage || ctx.accountTypeBefore === 'citizen';

  const returnUrl = encodeURIComponent(currentPathWithSearch);
  if (wasCitizen) {
    window.location.href = `/citizen/login?returnUrl=${returnUrl}`;
  } else if (isProPage && isNative) {
    window.location.href = `/pro/login?returnUrl=${returnUrl}`;
  } else {
    window.location.href = `/login?returnUrl=${returnUrl}`;
  }
});

const api = createAppApiClient({
  withCredentials: true,
  clientPlatform: isNative ? 'native' : undefined,
  // After a silent refresh, persist the new access token (and, on native, the
  // rotated refresh token). Web's refresh token stays in the HttpOnly cookie.
  setAccessToken,
  getRefreshToken,
  setRefreshToken,
  onUnauthorized: ({ currentPath, reason }) => {
    if (isPublicPath(currentPath)) {
      clearAuthSession();
      return;
    }

    const isCitizenPage = currentPath.startsWith('/citizen/');
    const accountType = localStorage.getItem('accountType');
    const shouldClearAccountType = isCitizenPage || accountType === 'citizen';

    // One funnel: clears the session, records the reason for the login banner,
    // wipes the offline cache, and navigates via the handler above (deduped).
    endSession(reason, { clearAccountType: shouldClearAccountType });
  },
});

// Research data is paywalled server-side (#1191): an unsubscribed caller gets
// 402 SUBSCRIPTION_REQUIRED. Callers each have their own generic failure text,
// so say it once, here, in the server's words. Web only — the native shell has
// its own surfaces and must not change for web work.
export const SUBSCRIPTION_REQUIRED_TOAST_ID = 'subscription-required';
export const SUBSCRIPTION_REQUIRED_MESSAGE = 'Subscription required to access this content';

export function notifySubscriptionRequired(error: unknown): void {
  const response = (error as { response?: { status?: number; data?: { code?: string; error?: string } } })?.response;
  if (response?.status === 402 && response.data?.code === 'SUBSCRIPTION_REQUIRED') {
    toast.error(response.data.error || SUBSCRIPTION_REQUIRED_MESSAGE, {
      id: SUBSCRIPTION_REQUIRED_TOAST_ID, // N parallel lookups -> one toast
    });
  }
}

if (!isNative) {
  api.interceptors?.response?.use(undefined, (error) => {
    notifySubscriptionRequired(error);
    return Promise.reject(error);
  });
}

export default api;
